import React, { useState, useEffect } from "react";
import { projects } from "../data";
import ProjectCard from "./ProjectCard";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";

const projectVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.1,
      duration: 0.6,
      ease: "easeOut",
    },
  }),
};

export default function Projects() {
  const [selectedProject, setSelectedProject] = useState(null);
  const controls = useAnimation();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  useEffect(() => {
    if (inView) {
      controls.start("visible");
    }
  }, [controls, inView]);

  // Extract tech stack from subtitle
  const getTechStack = (subtitle) => {
    return subtitle.split(", ").slice(0, 3); // Show first 3 technologies
  };

  return (
    <motion.section
      id="projects"
      ref={ref}
      initial="hidden"
      animate={controls}
      className="flex items-center justify-center bg-gray-800 text-white pt-6 pb-8 md:pt-12 md:pb-12 w-full px-4 sm:px-6 md:px-8 scroll-mt-16"
    >
      <div className="w-full max-w-7xl container mx-auto p-4 md:p-12 flex flex-col items-start space-y-4 md:space-y-6 font-ntr">
        <div className="flex flex-col w-full mb-2 md:mb-4 text-left">
          <h2 className="text-4xl mb-4 md:mb-5 font-bold text-left w-full md:w-auto">
            <span className="text-custom-accent">/ </span>My Projects
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 w-full">
          {projects.map((project, index) => {
            const techStack = getTechStack(project.subtitle);
            return (
              <motion.div
                key={project.image}
                className="group relative bg-gradient-to-br from-gray-700/50 to-gray-800/50 backdrop-blur-sm border border-gray-600/50 rounded-xl overflow-hidden cursor-pointer transition-all duration-300 hover:border-custom-accent/50 hover:shadow-2xl hover:shadow-custom-accent/20"
                custom={index}
                variants={projectVariants}
                onClick={() => setSelectedProject(project)}
                whileHover={{ y: -8 }}
              >
                {/* Image Container */}
                <div className="relative w-full h-48 overflow-hidden bg-gray-900">
                  <img
                    alt={project.title}
                    className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-110"
                    src={project.image}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
                
                {/* Content */}
                <div className="p-5 space-y-3">
                  <div>
                    <h1 className="text-white text-xl font-semibold mb-2 group-hover:text-custom-accent transition-colors duration-300">
                      {project.title}
                    </h1>
                    <p className="text-gray-400 text-sm line-clamp-2">
                      {project.description}
                    </p>
                  </div>
                  
                  {/* Tech Stack Tags */}
                  <div className="flex flex-wrap gap-2 pt-2">
                    {techStack.map((tech, techIndex) => (
                      <span
                        key={techIndex}
                        className="text-xs px-2 py-1 bg-custom-accent/10 text-custom-accent border border-custom-accent/20 rounded-md"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Hover overlay */}
                <div className="absolute inset-0 bg-custom-accent/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
              </motion.div>
            );
          })}
        </div>
        {selectedProject && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
            onClick={() => setSelectedProject(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="w-full max-w-4xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <ProjectCard
                project={selectedProject}
                onBack={() => setSelectedProject(null)}
              />
            </motion.div>
          </motion.div>
        )}
      </div>
    </motion.section>
  );
}
